import { appAlert } from '@/lib/appAlert';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { PortfolioTutorial } from '@/components/PortfolioTutorial';
import { ServicesManager } from '@/components/ServicesManager';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { SearchablePickerField } from '@/components/ui/SearchablePickerField';
import { WizardLayout } from '@/components/ui/WizardLayout';
import { fetchMySecondaryCategories, addSecondaryCategory, removeSecondaryCategory, MAX_SECONDARY_CATEGORIES } from '@/lib/secondaryCategories';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { useTheme } from '@/contexts/ThemeContext';
import { pickAndUploadLogo } from '@/lib/avatar';
import { getErrorMessage } from '@/lib/functions';
import { useKeyboardHeight } from '@/lib/useKeyboardHeight';
import { cancelSponsorship, getMySponsoredListing, getSponsorTopRate, SPONSORED_INK_PER_DAY, startOrUpdateSponsorship, type MySponsoredListing, type SponsoredTier } from '@/lib/professionals';
import { regionMatchesQuery } from '@/lib/regionTowns';
import { supabase } from '@/lib/supabase';
import { searchProfilesByName, type SearchProfileResult } from '@/lib/profiles';
import { findProfileByEmail } from '@/lib/agreements';
import type { Profile } from '@/lib/database.types';
import {
  cancelBusinessInvite,
  createBusiness,
  inviteBusinessMember,
  invitePartnerByEmail,
  leaveBusiness,
  listMyBusinesses,
  listMyPendingBusinessInvites,
  listMySentBusinessInvites,
  removeBusinessMember,
  respondBusinessInvite,
  setBusinessSearchIdentity,
  type MyBusiness,
  type PendingBusinessInvite,
  type SentBusinessInvite,
} from '@/lib/businesses';

function InfoButton({ message }: { message: string }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  return (
    <Pressable onPress={() => appAlert(t('common.info'), message)} hitSlop={10} accessibilityRole="button">
      <Ionicons name="information-circle-outline" size={16} color={colors.textMuted} />
    </Pressable>
  );
}

function cleanIbans(rows: { bank: string; iban: string }[]): { bank: string | null; iban: string }[] {
  return rows
    .map((r) => ({ bank: r.bank.trim() || null, iban: r.iban.trim() }))
    .filter((r) => !!r.iban);
}

const TUTORIAL_KEY = 'portfolio_tutorial_shown';

export default function ProfessionalAccountScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { user } = useAuth();
  const { profile, save, refresh } = useProfile();
  const router = useRouter();
  const { openSponsor } = useLocalSearchParams<{ openSponsor?: string }>();
  const insets = useSafeAreaInsets();
  // Αντί για KeyboardAvoidingView (behavior 'height' προκαλεί flicker σε
  // Android modals μέσα σε ήδη-Modal — layout↔keyboard geometry feedback
  // loop, βλ. SearchablePickerField). Χρησιμοποιείται στα region/business/
  // partner modals παρακάτω για να σπρώξει την κάρτα προς τα πάνω χωρίς resize.
  const keyboardHeight = useKeyboardHeight();

  const [logoBusy, setLogoBusy] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const floatAnim = useRef(new Animated.Value(0)).current;

  // ── Επιχειρήσεις (συνέταιροι) — ο χρήστης μπορεί να είναι μέλος (όχι ιδιοκτήτης) περισσότερων από μία. ──
  const [myBusiness, setMyBusiness] = useState<MyBusiness[]>([]);
  const [pendingBusinessInvites, setPendingBusinessInvites] = useState<PendingBusinessInvite[]>([]);
  const [sentBusinessInvites, setSentBusinessInvites] = useState<SentBusinessInvite[]>([]);
  const [businessLoading, setBusinessLoading] = useState(true);
  const [respondingInviteId, setRespondingInviteId] = useState<string | null>(null);
  const [cancellingInviteId, setCancellingInviteId] = useState<string | null>(null);
  const [showCreateBusiness, setShowCreateBusiness] = useState(false);
  const [newBusinessName, setNewBusinessName] = useState('');
  const [creatingBusiness, setCreatingBusiness] = useState(false);
  const [showInvitePartner, setShowInvitePartner] = useState(false);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [partnerResults, setPartnerResults] = useState<SearchProfileResult[]>([]);
  const [invitingPartnerId, setInvitingPartnerId] = useState<string | null>(null);
  const [invitingByEmail, setInvitingByEmail] = useState(false);
  const [partnerEquityPercent, setPartnerEquityPercent] = useState('');
  const partnerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [identityBusy, setIdentityBusy] = useState<string | null>(null);

  /** Επιλογή ενεργής ταυτότητας: null = προσωπικό επάγγελμα, ή business_id
   * μιας εκ των επιχειρήσεων του χρήστη — αμοιβαία αποκλειόμενα, ποτέ πάνω
   * από μία ενεργή ταυτόχρονα. */
  async function onSelectIdentity(targetBusinessId: string | null, currentBusinessId: string | undefined) {
    if (targetBusinessId === (currentBusinessId ?? null)) return;
    setIdentityBusy(targetBusinessId ?? 'personal');
    try {
      if (currentBusinessId) await setBusinessSearchIdentity(currentBusinessId, false);
      if (targetBusinessId) await setBusinessSearchIdentity(targetBusinessId, true);
      await loadBusiness();
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setIdentityBusy(null);
    }
  }

  const loadBusiness = async () => {
    setBusinessLoading(true);
    try {
      const [business, invites, sent] = await Promise.all([
        listMyBusinesses(),
        listMyPendingBusinessInvites(),
        listMySentBusinessInvites(),
      ]);
      setMyBusiness(business);
      setPendingBusinessInvites(invites);
      setSentBusinessInvites(sent);
    } catch {
      setMyBusiness([]);
      setPendingBusinessInvites([]);
      setSentBusinessInvites([]);
    } finally {
      setBusinessLoading(false);
    }
  };

  async function onCancelInvite(inviteId: string) {
    setCancellingInviteId(inviteId);
    try {
      await cancelBusinessInvite(inviteId);
      await loadBusiness();
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setCancellingInviteId(null);
    }
  }

  useEffect(() => { void loadBusiness(); }, []);

  useEffect(() => {
    const q = partnerQuery.trim();
    if (partnerSearchTimer.current) clearTimeout(partnerSearchTimer.current);
    if (q.length < 2) { setPartnerResults([]); return; }
    // Ίδια σύμβαση με όλες τις αναζητήσεις χρηστών στην εφαρμογή: '@' σημαίνει
    // αναζήτηση με email αντί για όνομα.
    if (q.includes('@')) {
      if (q.length < 5) { setPartnerResults([]); return; }
      partnerSearchTimer.current = setTimeout(async () => {
        try {
          const found = await findProfileByEmail(q);
          setPartnerResults(found && found.id !== user?.id ? [found as Profile] : []);
        } catch { setPartnerResults([]); }
      }, 400);
      return () => { if (partnerSearchTimer.current) clearTimeout(partnerSearchTimer.current); };
    }
    partnerSearchTimer.current = setTimeout(async () => {
      try {
        const results = await searchProfilesByName(q, user?.id);
        setPartnerResults(results.slice(0, 5));
      } catch { setPartnerResults([]); }
    }, 350);
    return () => { if (partnerSearchTimer.current) clearTimeout(partnerSearchTimer.current); };
  }, [partnerQuery, user?.id]);

  // Πατώντας "Έχω συνεταίρους" στο wizard δεν πρέπει να ξαναζητάει όνομα
  // επιχείρησης — το έχει ήδη δώσει στο βήμα 1. Δημιουργεί την επιχείρηση με
  // αυτό το όνομα (αν δεν υπάρχει ήδη) και πάει κατευθείαν στην πρόσκληση.
  async function onWizardHasPartners() {
    if (myBusiness.length > 0) { setShowInvitePartner(true); return; }
    const name = businessName.trim();
    if (!name) {
      appAlert(t('common.error'), t('profile.business_needNameFirst'));
      return;
    }
    setCreatingBusiness(true);
    try {
      await createBusiness(name);
      await loadBusiness();
      setShowInvitePartner(true);
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setCreatingBusiness(false);
    }
  }

  async function onCreateBusiness() {
    const name = newBusinessName.trim();
    if (!name) return;
    setCreatingBusiness(true);
    try {
      await createBusiness(name);
      setShowCreateBusiness(false);
      setNewBusinessName('');
      await loadBusiness();
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setCreatingBusiness(false);
    }
  }

  function parsedEquityPercent(): number | null {
    const raw = partnerEquityPercent.trim().replace(',', '.');
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || n > 100) return null;
    return n;
  }

  async function onInvitePartner(profileId: string) {
    setInvitingPartnerId(profileId);
    try {
      await inviteBusinessMember(profileId, parsedEquityPercent());
      setShowInvitePartner(false);
      setPartnerQuery('');
      setPartnerResults([]);
      setPartnerEquityPercent('');
      appAlert(t('common.success'), t('profile.business_inviteSent'));
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setInvitingPartnerId(null);
    }
  }

  async function onInvitePartnerByEmail() {
    const email = partnerQuery.trim();
    setInvitingByEmail(true);
    try {
      await invitePartnerByEmail(email, parsedEquityPercent());
      setShowInvitePartner(false);
      setPartnerQuery('');
      setPartnerResults([]);
      setPartnerEquityPercent('');
      appAlert(t('common.success'), t('profile.business_inviteSent'));
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setInvitingByEmail(false);
    }
  }

  async function onRespondInvite(inviteId: string, accept: boolean) {
    setRespondingInviteId(inviteId);
    try {
      await respondBusinessInvite(inviteId, accept);
      await loadBusiness();
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setRespondingInviteId(null);
    }
  }

  function onLeaveBusiness(businessId: string, businessLabel: string) {
    appAlert(t('profile.business_leaveConfirmTitle'), t('profile.business_leaveConfirmBody', { business: businessLabel }), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('profile.business_leaveAction'),
        style: 'destructive',
        onPress: async () => {
          try {
            await leaveBusiness(businessId);
            await loadBusiness();
          } catch (e) {
            appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
          }
        },
      },
    ]);
  }

  function onRemoveMember(profileId: string, name: string) {
    appAlert(t('profile.business_removeConfirmTitle'), t('profile.business_removeConfirmBody', { name }), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.remove'),
        style: 'destructive',
        onPress: async () => {
          try {
            await removeBusinessMember(profileId);
            await loadBusiness();
          } catch (e) {
            appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
          }
        },
      },
    ]);
  }

  // Ο χρήστης μπορεί να δημιουργήσει (owner) μία μόνο δική του επιχείρηση.
  const ownedBusiness = myBusiness.find((b) => b.my_role === 'owner');
  const isBusinessOwner = !!ownedBusiness;

  const [portfolioEnabled, setPortfolioEnabled] = useState(!!profile?.portfolio_enabled);
  const [portfolioPublic, setPortfolioPublic] = useState(profile?.portfolio_public !== false);
  const [specialty, setSpecialty] = useState(profile?.specialty ?? '');
  const [region, setRegion] = useState<string | null>((profile as any)?.region ?? null);
  const [regions, setRegions] = useState<string[]>([]);
  const [showRegionPicker, setShowRegionPicker] = useState(false);
  const [regionQuery, setRegionQuery] = useState('');
  const [professionCategoryId, setProfessionCategoryId] = useState<number | null>((profile as any)?.profession_category_id ?? null);
  const [professionCategories, setProfessionCategories] = useState<{ id: number; name: string }[]>([]);
  const [secondaryCategories, setSecondaryCategories] = useState<{ id: number; name: string }[]>([]);
  const [addingSecondaryCategoryId, setAddingSecondaryCategoryId] = useState<number | null>(null);
  const [secondaryCategoryBusy, setSecondaryCategoryBusy] = useState(false);
  const [businessName, setBusinessName] = useState(profile?.business_name ?? '');
  const [showBusinessName, setShowBusinessName] = useState(!!profile?.show_business_name);
  const [useLogoAsAvatar, setUseLogoAsAvatar] = useState(!!profile?.use_logo_as_avatar);
  const [professionalTerms, setProfessionalTerms] = useState((profile as any)?.professional_terms ?? '');
  const [businessAddress, setBusinessAddress] = useState(profile?.business_address ?? '');
  const [businessAddressPublic, setBusinessAddressPublic] = useState((profile as any)?.business_address_public !== false);
  const [businessCity, setBusinessCity] = useState(profile?.business_city ?? '');
  const [businessPostalCode, setBusinessPostalCode] = useState(profile?.business_postal_code ?? '');
  const [businessPhone, setBusinessPhone] = useState(profile?.business_phone ?? '');
  const [businessEmail, setBusinessEmail] = useState(profile?.business_email ?? '');
  const [businessVatNumber, setBusinessVatNumber] = useState(profile?.business_vat_number ?? '');
  const [paymentAccountHolderName, setPaymentAccountHolderName] = useState((profile as any)?.payment_account_holder_name ?? '');
  const [paymentIbans, setPaymentIbans] = useState<{ bank: string; iban: string }[]>(
    ((profile as any)?.payment_ibans ?? []).length > 0
      ? (profile as any).payment_ibans
      : [{ bank: '', iban: '' }],
  );
  const [paymentIris, setPaymentIris] = useState((profile as any)?.payment_iris ?? '');
  const [paymentRevolutLink, setPaymentRevolutLink] = useState((profile as any)?.payment_revolut_link ?? '');
  const [paymentVivaLink, setPaymentVivaLink] = useState((profile as any)?.payment_viva_link ?? '');
  const [eshopUrl, setEshopUrl] = useState((profile as any)?.eshop_url ?? '');
  const [savingDirect, setSavingDirect] = useState(false);

  useEffect(() => {
    void supabase.from('greek_regions').select('name').order('name')
      .then(({ data }) => { if (data) setRegions((data as { name: string }[]).map((r) => r.name)); });
    void supabase.from('profession_categories').select('id, name').order('name')
      .then(({ data }) => { if (data) setProfessionCategories(data as { id: number; name: string }[]); });
  }, []);

  const loadSecondaryCategories = async () => {
    if (!user) return;
    try {
      setSecondaryCategories(await fetchMySecondaryCategories(user.id));
    } catch {
      setSecondaryCategories([]);
    }
  };

  useEffect(() => { void loadSecondaryCategories(); }, [user?.id]);

  async function onAddSecondaryCategory(categoryId: number) {
    if (!user) return;
    setSecondaryCategoryBusy(true);
    try {
      await addSecondaryCategory(user.id, categoryId);
      await loadSecondaryCategories();
      setAddingSecondaryCategoryId(null);
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setSecondaryCategoryBusy(false);
    }
  }

  async function onRemoveSecondaryCategory(categoryId: number) {
    if (!user) return;
    setSecondaryCategoryBusy(true);
    try {
      await removeSecondaryCategory(user.id, categoryId);
      await loadSecondaryCategories();
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setSecondaryCategoryBusy(false);
    }
  }

  function selectRegion(name: string | null) {
    setRegion(name);
    setShowRegionPicker(false);
    setRegionQuery('');
  }

  const filteredRegions = useMemo(() => {
    if (!regionQuery.trim()) return regions;
    return regions.filter((r) => regionMatchesQuery(r, regionQuery));
  }, [regions, regionQuery]);

  const directDirty =
    specialty !== (profile?.specialty ?? '') ||
    region !== ((profile as any)?.region ?? null) ||
    professionCategoryId !== ((profile as any)?.profession_category_id ?? null) ||
    businessName !== (profile?.business_name ?? '') ||
    showBusinessName !== !!profile?.show_business_name ||
    useLogoAsAvatar !== !!profile?.use_logo_as_avatar ||
    professionalTerms !== ((profile as any)?.professional_terms ?? '') ||
    businessAddress !== (profile?.business_address ?? '') ||
    businessAddressPublic !== ((profile as any)?.business_address_public !== false) ||
    businessCity !== (profile?.business_city ?? '') ||
    businessPostalCode !== (profile?.business_postal_code ?? '') ||
    businessPhone !== (profile?.business_phone ?? '') ||
    businessEmail !== (profile?.business_email ?? '') ||
    businessVatNumber !== (profile?.business_vat_number ?? '') ||
    paymentAccountHolderName !== ((profile as any)?.payment_account_holder_name ?? '') ||
    JSON.stringify(cleanIbans(paymentIbans)) !== JSON.stringify((profile as any)?.payment_ibans ?? []) ||
    paymentIris !== ((profile as any)?.payment_iris ?? '') ||
    paymentRevolutLink !== ((profile as any)?.payment_revolut_link ?? '') ||
    paymentVivaLink !== ((profile as any)?.payment_viva_link ?? '') ||
    eshopUrl !== ((profile as any)?.eshop_url ?? '');

  useEffect(() => {
    Animated.timing(floatAnim, {
      toValue: directDirty ? 1 : 0,
      duration: 260,
      easing: Easing.out(Easing.back(1.4)),
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [directDirty, floatAnim]);

  async function saveDirectFields() {
    if (savingDirect) return;
    setSavingDirect(true);
    try {
      await save({
        specialty: specialty.trim() || null,
        region,
        profession_category_id: professionCategoryId,
        business_name: businessName.trim() || null,
        show_business_name: showBusinessName,
        use_logo_as_avatar: useLogoAsAvatar,
        professional_terms: professionalTerms.trim() || null,
        business_address: businessAddress.trim() || null,
        business_address_public: businessAddressPublic,
        business_city: businessCity.trim() || null,
        business_postal_code: businessPostalCode.trim() || null,
        business_phone: businessPhone.trim() || null,
        business_email: businessEmail.trim() || null,
        business_vat_number: businessVatNumber.trim() || null,
        payment_account_holder_name: paymentAccountHolderName.trim() || null,
        payment_ibans: cleanIbans(paymentIbans),
        payment_iris: paymentIris.trim() || null,
        payment_revolut_link: paymentRevolutLink.trim() || null,
        payment_viva_link: paymentVivaLink.trim() || null,
        eshop_url: eshopUrl.trim() || null,
      } as any);
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setSavingDirect(false);
    }
  }

  async function changeLogo() {
    if (!user) return;
    setLogoBusy(true);
    try {
      const url = await pickAndUploadLogo(user.id);
      if (url) await refresh();
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_logoUploadFailed')));
    } finally {
      setLogoBusy(false);
    }
  }

  async function togglePortfolioEnabled() {
    const next = !portfolioEnabled;
    setPortfolioEnabled(next);
    if (next) {
      const shown = await AsyncStorage.getItem(TUTORIAL_KEY);
      if (!shown) setShowTutorial(true);
    }
    await save({ portfolio_enabled: next });
  }

  async function togglePortfolioPublic() {
    const next = !portfolioPublic;
    setPortfolioPublic(next);
    await save({ portfolio_public: next });
  }

  // ── Προώθηση προφίλ στη λίστα αναζήτησης — συνδρομή με ημερήσια τιμή (bid),
  // χρεώνεται κάθε βράδυ για την επόμενη μέρα (καμία προπληρωμή). ──────────────
  const [sponsoredListing, setSponsoredListing] = useState<MySponsoredListing | null>(null);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [sponsorTier, setSponsorTier] = useState<SponsoredTier>('regional');
  const [sponsorRate, setSponsorRate] = useState(String(SPONSORED_INK_PER_DAY.regional));
  const [sponsorTopRate, setSponsorTopRate] = useState<number | null>(null);
  const [sponsorBusy, setSponsorBusy] = useState(false);
  const [sponsorError, setSponsorError] = useState<string | null>(null);

  function refreshSponsoredListing() {
    void getMySponsoredListing().then(setSponsoredListing).catch(() => {});
  }

  useEffect(() => {
    if (!portfolioEnabled) return;
    refreshSponsoredListing();
  }, [portfolioEnabled]);

  // Συντόμευση από την αρχική οθόνη ("Προώθηση επαγγελματικού προφίλ")
  const sponsorAutoOpened = useRef(false);
  useEffect(() => {
    if (sponsorAutoOpened.current) return;
    if (openSponsor === '1' && portfolioEnabled) {
      sponsorAutoOpened.current = true;
      setSponsorError(null);
      setShowSponsorModal(true);
    }
  }, [openSponsor, portfolioEnabled]);

  useEffect(() => {
    if (!showSponsorModal || !region) { setSponsorTopRate(null); return; }
    void getSponsorTopRate(region).then(setSponsorTopRate).catch(() => setSponsorTopRate(null));
  }, [showSponsorModal, region]);

  const sponsorRateFloor = SPONSORED_INK_PER_DAY[sponsorTier];
  const sponsorRateNum = Math.max(0, parseFloat(sponsorRate.replace(',', '.')) || 0);
  const sponsorOutbidRate = sponsorTopRate != null ? sponsorTopRate + 1 : null;

  function openSponsorModal() {
    if (sponsoredListing) {
      setSponsorTier(sponsoredListing.tier);
      setSponsorRate(String(sponsoredListing.daily_rate));
    } else {
      setSponsorTier('regional');
      setSponsorRate(String(SPONSORED_INK_PER_DAY.regional));
    }
    setSponsorError(null);
    setShowSponsorModal(true);
  }

  async function onActivateSponsor() {
    setSponsorError(null);
    if (sponsorRateNum < sponsorRateFloor) {
      setSponsorError(t('profile.sponsor_errorMinRate', { amount: sponsorRateFloor }));
      return;
    }
    setSponsorBusy(true);
    try {
      await startOrUpdateSponsorship(sponsorTier, sponsorRateNum);
      refreshSponsoredListing();
      setShowSponsorModal(false);
    } catch (e) {
      setSponsorError(getErrorMessage(e, t('profile.sponsor_errorGeneric')));
    } finally {
      setSponsorBusy(false);
    }
  }

  async function onCancelSponsor() {
    setSponsorBusy(true);
    try {
      await cancelSponsorship();
      setSponsoredListing(null);
      setShowSponsorModal(false);
    } catch (e) {
      setSponsorError(getErrorMessage(e, t('profile.sponsor_errorGeneric')));
    } finally {
      setSponsorBusy(false);
    }
  }

  function onTutorialDone() {
    void AsyncStorage.setItem(TUTORIAL_KEY, '1');
    void AsyncStorage.setItem('prompt_first_offer', '1');
    setShowTutorial(false);
  }

  // ── Wizard πρώτης ρύθμισης — βήμα-βήμα setup ολόκληρου του επαγγελματικού
  // λογαριασμού. Τα βήματα διαβάζουν/γράφουν απευθείας τα ίδια state πεδία
  // με την κύρια οθόνη (businessName, specialty, κλπ.) ώστε η αποθήκευση στο
  // τέλος να είναι απλά ένα saveDirectFields() — καμία διπλή πηγή αλήθειας. ──
  const WIZARD_TOTAL_STEPS = 6;
  const [showPortfolioWizard, setShowPortfolioWizard] = useState(false);
  const [portfolioWizardStep, setPortfolioWizardStep] = useState(1);
  const [wizardLogoLoading, setWizardLogoLoading] = useState(false);
  const [wizardSaving, setWizardSaving] = useState(false);
  const wizardAutoOpened = useRef(false);

  function openPortfolioWizard() {
    setPortfolioWizardStep(1);
    setShowPortfolioWizard(true);
  }

  // Τρέχει αυτόματα όταν ανοίγει η οθόνη, για όποιον έχει ενεργό επαγγελματικό
  // λογαριασμό αλλά δεν έχει ολοκληρώσει το setup — ΚΑΙ για ήδη ενεργούς
  // επαγγελματίες (professional_setup_done ξεκινάει false για όλους).
  useEffect(() => {
    if (wizardAutoOpened.current) return;
    if (profile?.portfolio_enabled && profile?.professional_setup_done === false) {
      wizardAutoOpened.current = true;
      openPortfolioWizard();
    }
  }, [profile]);

  async function finishPortfolioWizard() {
    setWizardSaving(true);
    try {
      await saveDirectFields();
      await save({ professional_setup_done: true });
      setShowPortfolioWizard(false);
    } finally {
      setWizardSaving(false);
    }
  }

  function skipPortfolioWizard() {
    setShowPortfolioWizard(false);
    void save({ professional_setup_done: true });
  }

  return (
    <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        style={styles.scrollFlex}
        contentContainerStyle={[styles.content, { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xxl + spacing.xl }]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.topBar}>
          <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
            <Text style={styles.back}>{t('common.back')}</Text>
          </Pressable>
          <Text style={styles.title}>{t('profile.section_professional')}</Text>
          <View style={styles.spacer} />
        </View>

        <View style={styles.profileModeTabs}>
          <Pressable
            style={styles.profileModeTab}
            onPress={() => router.back()}
            accessibilityRole="button"
          >
            <Ionicons name="person-outline" size={15} color={colors.textMuted} />
            <Text style={styles.profileModeTabText}>{t('profile.tab_personal')}</Text>
          </Pressable>
          <View style={[styles.profileModeTab, styles.profileModeTabActive]}>
            <Ionicons name="briefcase-outline" size={15} color={colors.textOnPrimary} />
            <Text style={[styles.profileModeTabText, styles.profileModeTabTextActive]}>{t('profile.tab_professional')}</Text>
          </View>
        </View>

        <Pressable style={styles.toggleRow} onPress={togglePortfolioEnabled} accessibilityRole="switch">
          <Text style={styles.toggleLabel}>
            {portfolioEnabled ? t('profile.professionalAccount_active') : t('profile.professionalAccount_activate')}
          </Text>
          <View style={[styles.toggle, portfolioEnabled && styles.toggleOn]}>
            <View style={[styles.knob, portfolioEnabled && styles.knobOn]} />
          </View>
        </Pressable>

        {pendingBusinessInvites.map((inv) => (
          <View key={inv.invite_id} style={styles.businessInviteCard}>
            <Text style={styles.businessInviteText}>
              {t('profile.business_inviteReceived', { business: inv.business_name, name: inv.invited_by_name || t('common.user') })}
            </Text>
            <View style={styles.businessInviteActions}>
              <Pressable
                style={styles.businessInviteDecline}
                disabled={respondingInviteId === inv.invite_id}
                onPress={() => void onRespondInvite(inv.invite_id, false)}
              >
                <Text style={styles.businessInviteDeclineText}>{t('common.decline')}</Text>
              </Pressable>
              <Pressable
                style={styles.businessInviteAccept}
                disabled={respondingInviteId === inv.invite_id}
                onPress={() => void onRespondInvite(inv.invite_id, true)}
              >
                <Text style={styles.businessInviteAcceptText}>{t('common.accept')}</Text>
              </Pressable>
            </View>
          </View>
        ))}

        {portfolioEnabled && (
          <>
            <Pressable style={styles.toggleRow} onPress={togglePortfolioPublic} accessibilityRole="switch">
              <View style={styles.toggleLabelRow}>
                <Ionicons
                  name={portfolioPublic ? 'globe-outline' : 'lock-closed-outline'}
                  size={15}
                  color={portfolioPublic ? colors.success : colors.textMuted}
                />
                <View>
                  <Text style={styles.toggleLabel}>{portfolioPublic ? t('common.public') : t('common.hidden')}</Text>
                  <Text style={styles.toggleSublabel}>
                    {portfolioPublic ? t('profile.portfolio_visibleAll') : t('profile.portfolio_visibleCircle')}
                  </Text>
                </View>
                <InfoButton message={portfolioPublic ? t('profile.info_portfolioPublic') : t('profile.info_portfolioPrivate')} />
              </View>
              <View style={[styles.toggle, portfolioPublic && styles.toggleOn]}>
                <View style={[styles.knob, portfolioPublic && styles.knobOn]} />
              </View>
            </Pressable>

            <Pressable
              style={styles.viewBtn}
              onPress={() => user?.id && router.push({ pathname: '/(app)/users/portfolio/[id]', params: { id: user.id } })}
              accessibilityRole="button"
            >
              <Ionicons name="eye-outline" size={14} color={colors.primary} />
              <Text style={styles.viewBtnText}>{t('profile.action_viewPortfolio')}</Text>
              <Ionicons name="chevron-forward" size={13} color={colors.textMuted} />
            </Pressable>

            <Pressable style={styles.wizardSetupBtn} onPress={openPortfolioWizard} accessibilityRole="button">
              <Ionicons name="settings-outline" size={15} color={colors.primary} />
              <Text style={styles.wizardSetupBtnText}>{t('profile.action_setupAccount')}</Text>
              <Ionicons name="chevron-forward" size={13} color={colors.textMuted} />
            </Pressable>

            {portfolioPublic && (
              <View style={styles.sponsorBox}>
                <View style={styles.sponsorHeader}>
                  <Ionicons name="megaphone-outline" size={16} color={colors.primary} />
                  <Text style={styles.sponsorTitle}>{t('profile.sponsor_title')}</Text>
                </View>
                {sponsoredListing ? (
                  <Text style={styles.sponsorActiveText}>
                    {t(sponsoredListing.tier === 'nationwide' ? 'profile.sponsor_activeNationwide' : 'profile.sponsor_activeRegional', {
                      rate: sponsoredListing.daily_rate,
                    })}
                  </Text>
                ) : (
                  <Text style={styles.sponsorSublabel}>{t('profile.sponsor_body')}</Text>
                )}
                <Pressable style={styles.sponsorBtn} onPress={openSponsorModal} accessibilityRole="button">
                  <Text style={styles.sponsorBtnText}>
                    {sponsoredListing ? t('profile.sponsor_actionEdit') : t('profile.sponsor_actionStart')}
                  </Text>
                </Pressable>
              </View>
            )}

            {/* ── Επιχείρηση (συνέταιροι) ── */}
            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.business_sectionTitle')}</Text>
                <InfoButton message={t('profile.business_info')} />
              </View>

              {!businessLoading && myBusiness.length === 0 && (
                <Pressable style={styles.logoUploadBtn} onPress={() => setShowCreateBusiness(true)} accessibilityRole="button">
                  <Ionicons name="people-outline" size={18} color={colors.primary} />
                  <Text style={styles.logoUploadBtnText}>{t('profile.business_create')}</Text>
                </Pressable>
              )}

              {myBusiness.length > 0 && (() => {
                const activeBusiness = myBusiness.find((biz) =>
                  biz.members.some((m) => m.member_id === user?.id && m.member_shows_as_business),
                );
                // Αν το προσωπικό business_name ταυτίζεται ήδη ακριβώς με μια
                // από τις επιχειρήσεις σου (π.χ. εσύ την ίδρυσες), η επιλογή
                // "Βασικό επάγγελμα" δεν προσφέρει καμία ουσιαστική διάκριση —
                // κρύβεται, και η ταυτόσημη επιχείρηση εμφανίζεται ως ενεργή.
                const identicalBusiness = myBusiness.find(
                  (biz) => biz.business_name.trim().toLowerCase() === businessName.trim().toLowerCase(),
                );
                const showPersonalCard = !identicalBusiness;
                const effectiveActiveBusinessId = activeBusiness?.business_id ?? identicalBusiness?.business_id;
                return (
                  <View style={styles.identityPickerBox}>
                    <Text style={styles.identityPickerTitle}>{t('profile.identityPicker_title')}</Text>
                    <Text style={styles.identityPickerHint}>{t('profile.identityPicker_hint')}</Text>

                    {showPersonalCard && (
                      <Pressable
                        style={[styles.identityCard, !activeBusiness && styles.identityCardSelected]}
                        disabled={identityBusy != null || !activeBusiness}
                        onPress={() => activeBusiness && void onSelectIdentity(null, activeBusiness.business_id)}
                        accessibilityRole="radio"
                        accessibilityState={{ checked: !activeBusiness }}
                      >
                        <Ionicons name="person" size={18} color={!activeBusiness ? colors.success : colors.textMuted} />
                        <View style={{ flex: 1 }}>
                          <Text style={[styles.identityCardTitle, !activeBusiness && styles.identityCardTitleSelected]}>
                            {t('profile.identityPicker_personal')}
                          </Text>
                          <Text style={styles.identityCardSubtitle} numberOfLines={1}>
                            {specialty.trim()
                              || professionCategories.find((c) => c.id === professionCategoryId)?.name
                              || businessName.trim()
                              || t('common.user')}
                          </Text>
                        </View>
                        {!activeBusiness && <Ionicons name="checkmark-circle" size={20} color={colors.success} />}
                      </Pressable>
                    )}

                    {myBusiness.map((biz) => {
                      const selected = effectiveActiveBusinessId === biz.business_id;
                      return (
                        <Pressable
                          key={biz.business_id}
                          style={[styles.identityCard, selected && styles.identityCardSelected]}
                          disabled={identityBusy != null || selected}
                          onPress={() => void onSelectIdentity(biz.business_id, activeBusiness?.business_id)}
                          accessibilityRole="radio"
                          accessibilityState={{ checked: selected }}
                        >
                          <Ionicons name="business" size={18} color={selected ? colors.success : colors.textMuted} />
                          <View style={{ flex: 1 }}>
                            <Text style={[styles.identityCardTitle, selected && styles.identityCardTitleSelected]} numberOfLines={1}>
                              {biz.business_name}
                            </Text>
                            <Text style={styles.identityCardSubtitle}>
                              {biz.my_role === 'owner' ? t('profile.business_ownedTag') : t('profile.business_partnerTag')}
                            </Text>
                          </View>
                          {selected && <Ionicons name="checkmark-circle" size={20} color={colors.success} />}
                        </Pressable>
                      );
                    })}
                  </View>
                );
              })()}

              {myBusiness.length > 0 && (
                <View style={{ gap: spacing.lg }}>
                  {[...myBusiness].sort((a, b) => (a.my_role === 'owner' ? -1 : 0) - (b.my_role === 'owner' ? -1 : 0)).map((biz) => {
                    const bizIsOwner = biz.my_role === 'owner';
                    return (
                      <View
                        key={biz.business_id}
                        style={[styles.businessCard, bizIsOwner ? styles.businessCardOwned : styles.businessCardPartner]}
                      >
                        <Text style={bizIsOwner ? styles.businessOwnedTag : styles.businessPartnerTag}>
                          {bizIsOwner ? t('profile.business_ownedTag') : t('profile.business_partnerTag')}
                        </Text>
                        <Text style={styles.businessNameHeading}>{biz.business_name}</Text>
                        {biz.members.map((m) => (
                          <View key={m.member_id} style={styles.businessMemberRow}>
                            <Avatar uri={m.member_avatar_url} name={m.member_name} size={32} />
                            <Text style={styles.businessMemberName} numberOfLines={1}>
                              {m.member_name || t('common.user')}{m.member_id === user?.id ? ` (${t('profile.business_you')})` : ''}
                            </Text>
                            {m.member_equity_percent != null && (
                              <Text style={styles.businessEquityText}>{m.member_equity_percent}%</Text>
                            )}
                            {m.member_role === 'owner' && (
                              <View style={styles.businessOwnerBadge}>
                                <Text style={styles.businessOwnerBadgeText}>{t('profile.business_owner')}</Text>
                              </View>
                            )}
                            {m.member_shows_as_business && (
                              <View style={styles.businessRepresentativeBadge}>
                                <Ionicons name="business" size={10} color={colors.success} />
                                <Text style={styles.businessRepresentativeBadgeText}>{t('profile.business_showsAsBusinessBadge')}</Text>
                              </View>
                            )}
                            {bizIsOwner && m.member_id !== user?.id && (
                              <Pressable hitSlop={8} onPress={() => onRemoveMember(m.member_id, m.member_name || '')}>
                                <Ionicons name="close-circle-outline" size={18} color={colors.textMuted} />
                              </Pressable>
                            )}
                          </View>
                        ))}
                        {bizIsOwner && sentBusinessInvites.filter((inv) => inv.business_id === biz.business_id).map((inv) => (
                          <View key={inv.invite_id} style={[styles.businessMemberRow, { opacity: 0.6 }]}>
                            <Avatar uri={inv.invited_avatar_url} name={inv.invited_name} size={32} />
                            <Text style={styles.businessMemberName} numberOfLines={1}>
                              {inv.invited_name || inv.invited_email || t('common.user')}
                            </Text>
                            <View style={styles.businessOwnerBadge}>
                              <Text style={styles.businessOwnerBadgeText}>{t('profile.business_invitedPending')}</Text>
                            </View>
                            <Pressable
                              hitSlop={8}
                              disabled={cancellingInviteId === inv.invite_id}
                              onPress={() => onCancelInvite(inv.invite_id)}
                            >
                              <Ionicons name="close-circle-outline" size={18} color={colors.textMuted} />
                            </Pressable>
                          </View>
                        ))}
                        {bizIsOwner && (
                          <Pressable style={styles.logoUploadBtn} onPress={() => setShowInvitePartner(true)} accessibilityRole="button">
                            <Ionicons name="person-add-outline" size={16} color={colors.primary} />
                            <Text style={styles.logoUploadBtnText}>{t('profile.business_invitePartner')}</Text>
                          </Pressable>
                        )}
                        <Pressable onPress={() => onLeaveBusiness(biz.business_id, biz.business_name)} accessibilityRole="button">
                          <Text style={styles.businessLeaveText}>{t('profile.business_leaveAction')}</Text>
                        </Pressable>
                      </View>
                    );
                  })}
                </View>
              )}
            </View>

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_region')}</Text>
                <InfoButton message={t('profile.info_region')} />
              </View>
              <Pressable style={styles.fieldInput} onPress={() => setShowRegionPicker(true)} accessibilityRole="button">
                <Text style={{ color: region ? colors.text : colors.textMuted, fontSize: fontSize.md }}>
                  {region ?? t('profile.region_select')}
                </Text>
              </Pressable>
            </View>

            {professionCategories.length > 0 && (
              <SearchablePickerField
                label={t('profile.field_professionCategory')}
                value={professionCategoryId}
                options={professionCategories}
                onChange={setProfessionCategoryId}
              />
            )}

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_specialty')}</Text>
                <InfoButton message={t('profile.info_specialty')} />
              </View>
              <TextInput
                style={styles.fieldInput}
                value={specialty}
                onChangeText={setSpecialty}
                placeholder={t('placeholder.specialty')}
                placeholderTextColor={colors.textMuted}
                maxLength={80}
                returnKeyType="done"
              />
            </View>

            {professionCategories.length > 0 && (
              <View style={styles.fieldSection}>
                <View style={styles.fieldLabelRow}>
                  <Text style={styles.fieldLabel}>{t('profile.field_secondaryCategories')}</Text>
                  <InfoButton message={t('profile.info_secondaryCategories')} />
                </View>
                {secondaryCategories.length > 0 && (
                  <View style={styles.secondaryCategoryChips}>
                    {secondaryCategories.map((c) => (
                      <View key={c.id} style={styles.secondaryCategoryChip}>
                        <Text style={styles.secondaryCategoryChipText}>{c.name}</Text>
                        <Pressable
                          hitSlop={8}
                          disabled={secondaryCategoryBusy}
                          onPress={() => onRemoveSecondaryCategory(c.id)}
                        >
                          <Ionicons name="close-circle" size={16} color={colors.textMuted} />
                        </Pressable>
                      </View>
                    ))}
                  </View>
                )}
                {secondaryCategories.length < MAX_SECONDARY_CATEGORIES && (
                  <SearchablePickerField
                    label={t('profile.field_addSecondaryCategory')}
                    value={addingSecondaryCategoryId}
                    options={professionCategories.filter(
                      (c) => c.id !== professionCategoryId && !secondaryCategories.some((sc) => sc.id === c.id),
                    )}
                    onChange={(id) => { if (id != null) void onAddSecondaryCategory(id); }}
                  />
                )}
              </View>
            )}

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessName')}</Text>
                <InfoButton message={t('profile.info_businessName')} />
              </View>
              <TextInput
                style={styles.fieldInput}
                value={businessName}
                onChangeText={setBusinessName}
                placeholder={t('profile.placeholder_businessName')}
                placeholderTextColor={colors.textMuted}
                maxLength={100}
                returnKeyType="done"
              />
              {!!businessName.trim() && (
                <Pressable style={styles.toggleRow} onPress={() => setShowBusinessName((v) => !v)} accessibilityRole="switch">
                  <Text style={styles.toggleLabel}>{t('profile.portfolio_showBusiness')}</Text>
                  <View style={[styles.toggle, showBusinessName && styles.toggleOn]}>
                    <View style={[styles.knob, showBusinessName && styles.knobOn]} />
                  </View>
                </Pressable>
              )}
            </View>

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessLogo')}</Text>
                <InfoButton message={t('profile.info_businessLogo')} />
              </View>
              {profile?.business_logo_url ? (
                <View style={styles.logoPreview}>
                  <Avatar uri={profile.business_logo_url} size={44} />
                  <Pressable onPress={() => void changeLogo()} disabled={logoBusy} hitSlop={8} style={styles.logoChangeBtn}>
                    <Text style={styles.logoChangeBtnText}>{logoBusy ? t('common.uploading') : t('profile.action_changeLogo')}</Text>
                  </Pressable>
                </View>
              ) : (
                <Pressable onPress={() => void changeLogo()} disabled={logoBusy} style={styles.logoUploadBtn} accessibilityRole="button">
                  <Ionicons name="image-outline" size={18} color={colors.primary} />
                  <Text style={styles.logoUploadBtnText}>{logoBusy ? t('common.uploading') : t('profile.action_addLogo')}</Text>
                </Pressable>
              )}
              {!!profile?.business_logo_url && (
                <Pressable style={styles.toggleRow} onPress={() => setUseLogoAsAvatar((v) => !v)} accessibilityRole="switch">
                  <Text style={styles.toggleLabel}>{t('profile.portfolio_useLogo')}</Text>
                  <View style={[styles.toggle, useLogoAsAvatar && styles.toggleOn]}>
                    <View style={[styles.knob, useLogoAsAvatar && styles.knobOn]} />
                  </View>
                </Pressable>
              )}
            </View>

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_professionalTerms')}</Text>
                <InfoButton message={t('profile.info_professionalTerms')} />
              </View>
              <TextInput
                style={[styles.fieldInput, { height: 120, paddingTop: spacing.sm }]}
                value={professionalTerms}
                onChangeText={setProfessionalTerms}
                placeholder={t('profile.placeholder_professionalTerms')}
                placeholderTextColor={colors.textMuted}
                multiline
                numberOfLines={6}
                textAlignVertical="top"
                maxLength={2000}
              />
              <Text style={styles.toggleSublabel}>{t('profile.hint_professionalTerms')}</Text>
            </View>

            {!!user?.id && <ServicesManager userId={user.id} />}

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_eshopUrl')}</Text>
                <InfoButton message={t('profile.info_eshopUrl')} />
              </View>
              <TextInput
                style={styles.fieldInput}
                value={eshopUrl}
                onChangeText={setEshopUrl}
                placeholder={t('profile.placeholder_eshopUrl')}
                placeholderTextColor={colors.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="url"
                maxLength={300}
                returnKeyType="done"
              />
              <Text style={styles.toggleSublabel}>{t('profile.hint_eshopUrl')}</Text>
            </View>

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessAddress')}</Text>
                <InfoButton message={t('profile.info_businessDetails')} />
              </View>
              <TextInput
                style={styles.fieldInput}
                value={businessAddress}
                onChangeText={setBusinessAddress}
                placeholder={t('profile.placeholder_businessAddress')}
                placeholderTextColor={colors.textMuted}
                maxLength={120}
                returnKeyType="done"
              />
              {!!businessAddress.trim() && (
                <Pressable
                  style={styles.addressVisibilityToggle}
                  onPress={() => setBusinessAddressPublic((v) => !v)}
                  accessibilityRole="switch"
                >
                  <Ionicons
                    name={businessAddressPublic ? 'globe-outline' : 'lock-closed-outline'}
                    size={14}
                    color={businessAddressPublic ? colors.success : colors.textMuted}
                  />
                  <Text style={styles.addressVisibilityToggleText}>
                    {businessAddressPublic ? t('profile.businessAddress_public') : t('profile.businessAddress_private')}
                  </Text>
                  <View style={[styles.toggle, businessAddressPublic && styles.toggleOn]}>
                    <View style={[styles.knob, businessAddressPublic && styles.knobOn]} />
                  </View>
                </Pressable>
              )}
            </View>

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessCity')}</Text>
              </View>
              <TextInput
                style={styles.fieldInput}
                value={businessCity}
                onChangeText={setBusinessCity}
                placeholder={t('profile.placeholder_businessCity')}
                placeholderTextColor={colors.textMuted}
                maxLength={60}
                returnKeyType="done"
              />
            </View>

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessPostalCode')}</Text>
              </View>
              <TextInput
                style={styles.fieldInput}
                value={businessPostalCode}
                onChangeText={setBusinessPostalCode}
                placeholder={t('profile.placeholder_businessPostalCode')}
                placeholderTextColor={colors.textMuted}
                keyboardType="number-pad"
                maxLength={10}
                returnKeyType="done"
              />
            </View>

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessPhone')}</Text>
              </View>
              <TextInput
                style={styles.fieldInput}
                value={businessPhone}
                onChangeText={setBusinessPhone}
                placeholder={t('profile.placeholder_businessPhone')}
                placeholderTextColor={colors.textMuted}
                keyboardType="phone-pad"
                maxLength={20}
                returnKeyType="done"
              />
            </View>

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessEmail')}</Text>
              </View>
              <TextInput
                style={styles.fieldInput}
                value={businessEmail}
                onChangeText={setBusinessEmail}
                placeholder={t('profile.placeholder_businessEmail')}
                placeholderTextColor={colors.textMuted}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                maxLength={100}
                returnKeyType="done"
              />
            </View>

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessVatNumber')}</Text>
              </View>
              <TextInput
                style={styles.fieldInput}
                value={businessVatNumber}
                onChangeText={setBusinessVatNumber}
                placeholder={t('profile.placeholder_businessVatNumber')}
                placeholderTextColor={colors.textMuted}
                keyboardType="number-pad"
                maxLength={15}
                returnKeyType="done"
              />
            </View>

            <View style={styles.fieldSection}>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>{t('profile.section_paymentMethods')}</Text>
                <InfoButton message={t('profile.info_paymentMethods')} />
              </View>
              <Text style={styles.toggleSublabel}>{t('profile.hint_paymentMethods')}</Text>

              <TextInput
                style={styles.fieldInput}
                value={paymentAccountHolderName}
                onChangeText={setPaymentAccountHolderName}
                placeholder={t('profile.placeholder_paymentAccountHolderName')}
                placeholderTextColor={colors.textMuted}
                maxLength={100}
                returnKeyType="done"
              />

              {paymentIbans.map((row, idx) => (
                <View key={idx} style={styles.ibanRow}>
                  <TextInput
                    style={[styles.fieldInput, styles.ibanBankInput]}
                    value={row.bank}
                    onChangeText={(v) => setPaymentIbans((prev) => prev.map((r, i) => (i === idx ? { ...r, bank: v } : r)))}
                    placeholder={t('profile.placeholder_bankName')}
                    placeholderTextColor={colors.textMuted}
                    maxLength={40}
                    returnKeyType="next"
                  />
                  <TextInput
                    style={[styles.fieldInput, styles.ibanIbanInput]}
                    value={row.iban}
                    onChangeText={(v) => setPaymentIbans((prev) => prev.map((r, i) => (i === idx ? { ...r, iban: v } : r)))}
                    placeholder={t('profile.placeholder_paymentIban')}
                    placeholderTextColor={colors.textMuted}
                    autoCapitalize="characters"
                    autoCorrect={false}
                    maxLength={34}
                    returnKeyType="done"
                  />
                  {paymentIbans.length > 1 && (
                    <Pressable
                      onPress={() => setPaymentIbans((prev) => prev.filter((_, i) => i !== idx))}
                      hitSlop={8}
                      style={styles.ibanRemoveBtn}
                    >
                      <Ionicons name="close-circle" size={20} color={colors.textMuted} />
                    </Pressable>
                  )}
                </View>
              ))}
              <Pressable
                style={styles.ibanAddBtn}
                onPress={() => setPaymentIbans((prev) => [...prev, { bank: '', iban: '' }])}
                accessibilityRole="button"
              >
                <Ionicons name="add" size={16} color={colors.primary} />
                <Text style={styles.ibanAddBtnText}>{t('profile.action_addBank')}</Text>
              </Pressable>

              <TextInput
                style={[styles.fieldInput, { marginTop: spacing.sm }]}
                value={paymentIris}
                onChangeText={setPaymentIris}
                placeholder={t('profile.placeholder_paymentIris')}
                placeholderTextColor={colors.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                maxLength={30}
                returnKeyType="done"
              />
              <TextInput
                style={[styles.fieldInput, { marginTop: spacing.sm }]}
                value={paymentRevolutLink}
                onChangeText={setPaymentRevolutLink}
                placeholder={t('profile.placeholder_paymentRevolut')}
                placeholderTextColor={colors.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="url"
                maxLength={300}
                returnKeyType="done"
              />
              <TextInput
                style={[styles.fieldInput, { marginTop: spacing.sm }]}
                value={paymentVivaLink}
                onChangeText={setPaymentVivaLink}
                placeholder={t('profile.placeholder_paymentViva')}
                placeholderTextColor={colors.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="url"
                maxLength={300}
                returnKeyType="done"
              />
            </View>
          </>
        )}
      </ScrollView>

      <Animated.View
        style={[
          styles.floatBtn,
          { bottom: insets.bottom + 16 },
          {
            transform: [{ translateY: floatAnim.interpolate({ inputRange: [0, 1], outputRange: [100, 0] }) }],
            opacity: floatAnim,
          },
        ]}
        pointerEvents={directDirty ? 'auto' : 'none'}
      >
        <Pressable style={styles.floatBtnInner} onPress={saveDirectFields} accessibilityRole="button">
          <Text style={styles.floatBtnIcon}>💾</Text>
          <Text style={styles.floatBtnText}>{savingDirect ? t('common.saving') : t('profile.action_saveShort')}</Text>
        </Pressable>
      </Animated.View>

      <PortfolioTutorial visible={showTutorial} onDone={onTutorialDone} />

      {showPortfolioWizard && (
      <View style={[StyleSheet.absoluteFillObject, { backgroundColor: colors.background }]}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView style={{ flex: 1, backgroundColor: colors.background }} contentContainerStyle={{ padding: spacing.lg, paddingTop: insets.top + spacing.md }} keyboardShouldPersistTaps="handled">
          <WizardLayout
            step={portfolioWizardStep}
            totalSteps={WIZARD_TOTAL_STEPS}
            title={t(`profile.setupWizard_step${portfolioWizardStep}_title`)}
            subtitle={t(`profile.setupWizard_step${portfolioWizardStep}_subtitle`)}
            onBack={portfolioWizardStep > 1 ? () => setPortfolioWizardStep((s) => s - 1) : undefined}
            onClose={skipPortfolioWizard}
          />

          {portfolioWizardStep === 1 && (
            <View style={{ gap: spacing.md }}>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessName')}</Text>
                <TextInput
                  style={styles.fieldInput}
                  value={businessName}
                  onChangeText={setBusinessName}
                  placeholder={t('profile.placeholder_businessName')}
                  placeholderTextColor={colors.textMuted}
                  maxLength={100}
                  returnKeyType="next"
                />
              </View>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_region')}</Text>
                <Pressable style={styles.fieldInput} onPress={() => setShowRegionPicker(true)} accessibilityRole="button">
                  <Text style={{ color: region ? colors.text : colors.textMuted, fontSize: fontSize.md }}>
                    {region ?? t('profile.region_select')}
                  </Text>
                </Pressable>
              </View>
              {professionCategories.length > 0 && (
                <SearchablePickerField
                  label={t('profile.field_professionCategory')}
                  value={professionCategoryId}
                  options={professionCategories}
                  onChange={setProfessionCategoryId}
                />
              )}
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_specialty')}</Text>
                <TextInput
                  style={styles.fieldInput}
                  value={specialty}
                  onChangeText={setSpecialty}
                  placeholder={t('placeholder.specialty')}
                  placeholderTextColor={colors.textMuted}
                  maxLength={80}
                  returnKeyType="done"
                />
              </View>
              <Button title={t('common.next')} onPress={() => setPortfolioWizardStep(2)} />
            </View>
          )}

          {portfolioWizardStep === 2 && (
            <View style={{ gap: spacing.md }}>
              {profile?.business_logo_url ? (
                <View style={styles.logoPreview}>
                  <Avatar uri={profile.business_logo_url} size={56} />
                  <Pressable
                    onPress={async () => { setWizardLogoLoading(true); try { await changeLogo(); } finally { setWizardLogoLoading(false); } }}
                    disabled={wizardLogoLoading}
                    hitSlop={8}
                    style={styles.logoChangeBtn}
                  >
                    <Text style={styles.logoChangeBtnText}>{wizardLogoLoading ? t('common.uploading') : t('profile.action_changeLogo')}</Text>
                  </Pressable>
                </View>
              ) : (
                <Pressable
                  onPress={async () => { setWizardLogoLoading(true); try { await changeLogo(); } finally { setWizardLogoLoading(false); } }}
                  disabled={wizardLogoLoading}
                  style={styles.logoUploadBtn}
                  accessibilityRole="button"
                >
                  <Ionicons name="image-outline" size={20} color={colors.primary} />
                  <Text style={styles.logoUploadBtnText}>{wizardLogoLoading ? t('common.uploading') : t('profile.action_addLogo')}</Text>
                </Pressable>
              )}
              <Button title={t('common.next')} onPress={() => setPortfolioWizardStep(3)} />
            </View>
          )}

          {portfolioWizardStep === 3 && (
            <View style={{ gap: spacing.md }}>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessAddress')}</Text>
                <TextInput style={styles.fieldInput} value={businessAddress} onChangeText={setBusinessAddress} placeholder={t('profile.placeholder_businessAddress')} placeholderTextColor={colors.textMuted} maxLength={120} />
              </View>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessCity')}</Text>
                <TextInput style={styles.fieldInput} value={businessCity} onChangeText={setBusinessCity} placeholder={t('profile.placeholder_businessCity')} placeholderTextColor={colors.textMuted} maxLength={60} />
              </View>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessPostalCode')}</Text>
                <TextInput style={styles.fieldInput} value={businessPostalCode} onChangeText={setBusinessPostalCode} placeholder={t('profile.placeholder_businessPostalCode')} placeholderTextColor={colors.textMuted} keyboardType="number-pad" maxLength={10} />
              </View>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessPhone')}</Text>
                <TextInput style={styles.fieldInput} value={businessPhone} onChangeText={setBusinessPhone} placeholder={t('profile.placeholder_businessPhone')} placeholderTextColor={colors.textMuted} keyboardType="phone-pad" maxLength={20} />
              </View>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessEmail')}</Text>
                <TextInput style={styles.fieldInput} value={businessEmail} onChangeText={setBusinessEmail} placeholder={t('profile.placeholder_businessEmail')} placeholderTextColor={colors.textMuted} keyboardType="email-address" autoCapitalize="none" maxLength={100} />
              </View>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_businessVatNumber')}</Text>
                <TextInput style={styles.fieldInput} value={businessVatNumber} onChangeText={setBusinessVatNumber} placeholder={t('profile.placeholder_businessVatNumber')} placeholderTextColor={colors.textMuted} keyboardType="number-pad" maxLength={15} />
              </View>
              <Button title={t('common.next')} onPress={() => setPortfolioWizardStep(4)} />
            </View>
          )}

          {portfolioWizardStep === 4 && (
            <View style={{ gap: spacing.md }}>
              <Text style={styles.toggleSublabel}>{t('profile.setupWizard_paymentsHint')}</Text>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_eshopUrl')}</Text>
                <TextInput style={styles.fieldInput} value={eshopUrl} onChangeText={setEshopUrl} placeholder={t('profile.placeholder_eshopUrl')} placeholderTextColor={colors.textMuted} autoCapitalize="none" keyboardType="url" maxLength={300} />
              </View>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_paymentAccountHolderName')}</Text>
                <TextInput style={styles.fieldInput} value={paymentAccountHolderName} onChangeText={setPaymentAccountHolderName} placeholder={t('profile.placeholder_paymentAccountHolderName')} placeholderTextColor={colors.textMuted} maxLength={100} />
              </View>
              {paymentIbans.map((row, idx) => (
                <View key={idx} style={styles.ibanRow}>
                  <TextInput
                    style={[styles.fieldInput, styles.ibanBankInput]}
                    value={row.bank}
                    onChangeText={(v) => setPaymentIbans((prev) => prev.map((r, i) => (i === idx ? { ...r, bank: v } : r)))}
                    placeholder={t('profile.placeholder_bankName')}
                    placeholderTextColor={colors.textMuted}
                    maxLength={40}
                  />
                  <TextInput
                    style={[styles.fieldInput, styles.ibanIbanInput]}
                    value={row.iban}
                    onChangeText={(v) => setPaymentIbans((prev) => prev.map((r, i) => (i === idx ? { ...r, iban: v } : r)))}
                    placeholder={t('profile.placeholder_paymentIban')}
                    placeholderTextColor={colors.textMuted}
                    autoCapitalize="characters"
                    maxLength={34}
                  />
                </View>
              ))}
              <Pressable style={styles.ibanAddBtn} onPress={() => setPaymentIbans((prev) => [...prev, { bank: '', iban: '' }])} accessibilityRole="button">
                <Ionicons name="add" size={16} color={colors.primary} />
                <Text style={styles.ibanAddBtnText}>{t('profile.action_addBank')}</Text>
              </Pressable>
              <TextInput style={styles.fieldInput} value={paymentIris} onChangeText={setPaymentIris} placeholder={t('profile.placeholder_paymentIris')} placeholderTextColor={colors.textMuted} autoCapitalize="none" maxLength={30} />
              <TextInput style={styles.fieldInput} value={paymentRevolutLink} onChangeText={setPaymentRevolutLink} placeholder={t('profile.placeholder_paymentRevolut')} placeholderTextColor={colors.textMuted} autoCapitalize="none" keyboardType="url" maxLength={300} />
              <TextInput style={styles.fieldInput} value={paymentVivaLink} onChangeText={setPaymentVivaLink} placeholder={t('profile.placeholder_paymentViva')} placeholderTextColor={colors.textMuted} autoCapitalize="none" keyboardType="url" maxLength={300} />
              <Button title={t('common.next')} onPress={() => setPortfolioWizardStep(5)} />
            </View>
          )}

          {portfolioWizardStep === 5 && (
            <View style={{ gap: spacing.md }}>
              <View style={styles.fieldSection}>
                <Text style={styles.fieldLabel}>{t('profile.field_professionalTerms')}</Text>
                <TextInput
                  style={[styles.fieldInput, { height: 110, paddingTop: spacing.sm }]}
                  value={professionalTerms}
                  onChangeText={setProfessionalTerms}
                  placeholder={t('profile.placeholder_professionalTerms')}
                  placeholderTextColor={colors.textMuted}
                  multiline
                  textAlignVertical="top"
                  maxLength={2000}
                />
              </View>

              {myBusiness.length > 0 ? (
                <>
                  {myBusiness.map((biz) => (
                    <View key={biz.business_id} style={{ gap: spacing.sm, marginBottom: spacing.md }}>
                      <Text style={styles.fieldLabel}>{t('profile.setupWizard_alreadyInBusiness', { business: biz.business_name })}</Text>
                      {biz.members.map((m) => (
                        <View key={m.member_id} style={styles.businessMemberRow}>
                          <Avatar uri={m.member_avatar_url} name={m.member_name} size={32} />
                          <Text style={styles.businessMemberName} numberOfLines={1}>
                            {m.member_name || t('common.user')}{m.member_id === user?.id ? ` (${t('profile.business_you')})` : ''}
                          </Text>
                          {m.member_role === 'owner' && (
                            <View style={styles.businessOwnerBadge}>
                              <Text style={styles.businessOwnerBadgeText}>{t('profile.business_owner')}</Text>
                            </View>
                          )}
                        </View>
                      ))}
                      {biz.my_role === 'owner' && (
                        <Pressable style={styles.logoUploadBtn} onPress={() => setShowInvitePartner(true)} accessibilityRole="button">
                          <Ionicons name="person-add-outline" size={16} color={colors.primary} />
                          <Text style={styles.logoUploadBtnText}>{t('profile.business_invitePartner')}</Text>
                        </Pressable>
                      )}
                    </View>
                  ))}
                </>
              ) : (
                <>
                  <Text style={styles.fieldLabel}>{t('profile.setupWizard_partnersQuestion')}</Text>
                  <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                    <View style={{ flex: 1 }}>
                      <Button title={t('profile.setupWizard_worksAlone')} variant="secondary" onPress={() => setPortfolioWizardStep(6)} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Button title={t('profile.setupWizard_hasPartners')} onPress={onWizardHasPartners} loading={creatingBusiness} />
                    </View>
                  </View>
                </>
              )}
              <Button title={t('common.next')} onPress={() => setPortfolioWizardStep(6)} />
            </View>
          )}

          {portfolioWizardStep === 6 && (
            <View style={{ gap: spacing.md }}>
              <Pressable style={styles.toggleRow} onPress={() => setPortfolioPublic((v) => !v)} accessibilityRole="switch">
                <View style={styles.toggleLabelRow}>
                  <Ionicons name={portfolioPublic ? 'globe-outline' : 'lock-closed-outline'} size={15} color={portfolioPublic ? colors.success : colors.textMuted} />
                  <View>
                    <Text style={styles.toggleLabel}>{portfolioPublic ? t('common.public') : t('common.hidden')}</Text>
                    <Text style={styles.toggleSublabel}>{portfolioPublic ? t('profile.portfolio_visibleAll') : t('profile.portfolio_visibleCircle')}</Text>
                  </View>
                </View>
                <View style={[styles.toggle, portfolioPublic && styles.toggleOn]}>
                  <View style={[styles.knob, portfolioPublic && styles.knobOn]} />
                </View>
              </Pressable>

              <View style={styles.wizardReviewCard}>
                <Text style={styles.wizardReviewTitle}>{t('profile.setupWizard_step6_title')}</Text>
                {!!businessName.trim() && (
                  <View style={styles.wizardReviewRow}>
                    <Text style={styles.wizardReviewLabel}>{t('profile.review_business')}</Text>
                    <Text style={styles.wizardReviewValue}>{businessName.trim()}</Text>
                  </View>
                )}
                {!!specialty.trim() && (
                  <View style={styles.wizardReviewRow}>
                    <Text style={styles.wizardReviewLabel}>{t('profile.review_specialty')}</Text>
                    <Text style={styles.wizardReviewValue}>{specialty.trim()}</Text>
                  </View>
                )}
                <View style={styles.wizardReviewRow}>
                  <Text style={styles.wizardReviewLabel}>{t('profile.review_visibility')}</Text>
                  <Text style={styles.wizardReviewValue}>{portfolioPublic ? t('common.public') : t('common.hidden')}</Text>
                </View>
                {!!profile?.business_logo_url && (
                  <View style={styles.wizardReviewRow}>
                    <Text style={styles.wizardReviewLabel}>{t('profile.review_logo')}</Text>
                    <Text style={styles.wizardReviewValue}>{t('common.uploaded')}</Text>
                  </View>
                )}
              </View>

              <Button title={wizardSaving ? t('profile.saving_portfolio') : t('profile.complete_portfolio')} onPress={finishPortfolioWizard} loading={wizardSaving} />
            </View>
          )}
        </ScrollView>
        </KeyboardAvoidingView>
      </View>
      )}

      {showRegionPicker && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setShowRegionPicker(false)}>
          <Pressable style={[styles.regionModalOverlay, { paddingBottom: keyboardHeight }]} onPress={() => setShowRegionPicker(false)}>
            <Pressable style={styles.regionModalCard} onPress={() => {}}>
              <Text style={styles.regionModalTitle}>{t('profile.field_region')}</Text>
              <View style={styles.regionSearchWrap}>
                <Ionicons name="search" size={15} color={colors.textMuted} />
                <TextInput
                  style={styles.regionSearchInput}
                  value={regionQuery}
                  onChangeText={setRegionQuery}
                  placeholder={t('profile.region_search_placeholder')}
                  placeholderTextColor={colors.textMuted}
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                {regionQuery.length > 0 && (
                  <Pressable onPress={() => setRegionQuery('')} hitSlop={8}>
                    <Ionicons name="close-circle" size={16} color={colors.textMuted} />
                  </Pressable>
                )}
              </View>
              <ScrollView style={{ maxHeight: 380 }} keyboardShouldPersistTaps="handled">
                {!regionQuery.trim() && (
                  <Pressable style={styles.regionRow} onPress={() => selectRegion(null)}>
                    <Text style={styles.regionRowText}>{t('profile.region_none')}</Text>
                    {region === null && <Ionicons name="checkmark" size={18} color={colors.primary} />}
                  </Pressable>
                )}
                {filteredRegions.map((name) => (
                  <Pressable key={name} style={styles.regionRow} onPress={() => selectRegion(name)}>
                    <Text style={styles.regionRowText}>{name}</Text>
                    {region === name && <Ionicons name="checkmark" size={18} color={colors.primary} />}
                  </Pressable>
                ))}
                {filteredRegions.length === 0 && (
                  <Text style={[styles.regionRowText, { textAlign: 'center', paddingVertical: spacing.md, color: colors.textMuted }]}>
                    {t('profile.region_notFound')}
                  </Text>
                )}
              </ScrollView>
              <Pressable style={styles.regionModalClose} onPress={() => setShowRegionPicker(false)}>
                <Text style={styles.regionModalCloseText}>{t('common.close')}</Text>
              </Pressable>
            </Pressable>
          </Pressable>
        </Modal>
      )}

      {showCreateBusiness && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setShowCreateBusiness(false)}>
          <Pressable style={[styles.regionModalOverlay, { paddingBottom: keyboardHeight }]} onPress={() => setShowCreateBusiness(false)}>
            <Pressable style={styles.regionModalCard} onPress={() => {}}>
              <Text style={styles.regionModalTitle}>{t('profile.business_create')}</Text>
              <Text style={styles.toggleSublabel}>{t('profile.business_createHint')}</Text>
              <TextInput
                style={styles.fieldInput}
                value={newBusinessName}
                onChangeText={setNewBusinessName}
                placeholder={t('profile.placeholder_businessName')}
                placeholderTextColor={colors.textMuted}
                maxLength={100}
                autoFocus
              />
              <Button title={t('common.create')} onPress={onCreateBusiness} loading={creatingBusiness} disabled={!newBusinessName.trim()} />
            </Pressable>
          </Pressable>
        </Modal>
      )}

      {showSponsorModal && (
        <Modal visible transparent animationType="fade" statusBarTranslucent onRequestClose={() => setShowSponsorModal(false)}>
          <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <Pressable style={styles.regionModalOverlay} onPress={() => setShowSponsorModal(false)}>
            <Pressable style={styles.regionModalCard} onPress={() => {}}>
              <Text style={styles.regionModalTitle}>{t('profile.sponsor_modalTitle')}</Text>
              <Text style={styles.toggleSublabel}>{t('profile.sponsor_modalHint')}</Text>

              <View style={styles.sponsorTierRow}>
                <Pressable
                  style={[styles.sponsorTierOption, sponsorTier === 'regional' && styles.sponsorTierOptionSelected]}
                  onPress={() => { setSponsorTier('regional'); setSponsorRate(String(Math.max(SPONSORED_INK_PER_DAY.regional, sponsorRateNum))); }}
                  accessibilityRole="button"
                >
                  <Text style={[styles.sponsorTierText, sponsorTier === 'regional' && styles.sponsorTierTextSelected]}>
                    {t('profile.sponsor_tierRegional', { region: region || t('profile.sponsor_tierNoRegion') })}
                  </Text>
                  <Text style={[styles.sponsorTierPrice, sponsorTier === 'regional' && styles.sponsorTierTextSelected]}>
                    {t('profile.sponsor_pricePerDayFrom', { amount: SPONSORED_INK_PER_DAY.regional })}
                  </Text>
                </Pressable>
                <Pressable
                  style={[styles.sponsorTierOption, sponsorTier === 'nationwide' && styles.sponsorTierOptionSelected]}
                  onPress={() => { setSponsorTier('nationwide'); setSponsorRate(String(Math.max(SPONSORED_INK_PER_DAY.nationwide, sponsorRateNum))); }}
                  accessibilityRole="button"
                >
                  <Text style={[styles.sponsorTierText, sponsorTier === 'nationwide' && styles.sponsorTierTextSelected]}>
                    {t('profile.sponsor_tierNationwide')}
                  </Text>
                  <Text style={[styles.sponsorTierPrice, sponsorTier === 'nationwide' && styles.sponsorTierTextSelected]}>
                    {t('profile.sponsor_pricePerDayFrom', { amount: SPONSORED_INK_PER_DAY.nationwide })}
                  </Text>
                </Pressable>
              </View>

              <View style={styles.sponsorWhereBox}>
                <Text style={styles.sponsorWhereTitle}>{t('profile.sponsor_whereTitle')}</Text>
                <View style={styles.sponsorWhereRow}>
                  <Ionicons name="checkmark-circle" size={14} color={colors.success} />
                  <Text style={styles.sponsorWhereText}>
                    {sponsorTier === 'nationwide'
                      ? t('profile.sponsor_whereListEverywhere')
                      : t('profile.sponsor_whereListRegion', { region: region || t('profile.sponsor_tierNoRegion') })}
                  </Text>
                </View>
                {sponsorTier === 'nationwide' && (
                  <View style={styles.sponsorWhereRow}>
                    <Ionicons name="checkmark-circle" size={14} color={colors.success} />
                    <Text style={styles.sponsorWhereText}>
                      {t('profile.sponsor_whereBanner', { region: region || t('profile.sponsor_tierNoRegion') })}
                    </Text>
                  </View>
                )}
              </View>

              <Text style={styles.toggleSublabel}>{t('profile.sponsor_rateLabel')}</Text>
              <View style={styles.regionSearchWrap}>
                <Ionicons name="flash-outline" size={15} color={colors.textMuted} />
                <TextInput
                  style={styles.regionSearchInput}
                  value={sponsorRate}
                  onChangeText={setSponsorRate}
                  placeholder={t('profile.sponsor_ratePlaceholder')}
                  placeholderTextColor={colors.textMuted}
                  keyboardType="decimal-pad"
                />
                <Text style={styles.sponsorRateSuffix}>{t('profile.sponsor_perDay')}</Text>
              </View>

              {sponsorOutbidRate != null && (
                <Pressable
                  style={styles.sponsorOutbidBtn}
                  onPress={() => setSponsorRate(String(sponsorOutbidRate))}
                  accessibilityRole="button"
                >
                  <Ionicons name="trending-up-outline" size={14} color={colors.primary} />
                  <Text style={styles.sponsorOutbidText}>
                    {t('profile.sponsor_outbid', { amount: sponsorOutbidRate })}
                  </Text>
                </Pressable>
              )}

              <Text style={styles.sponsorNightlyNote}>{t('profile.sponsor_nightlyNote')}</Text>
              {!!sponsorError && <Text style={styles.sponsorErrorText}>{sponsorError}</Text>}

              <Button
                title={sponsoredListing ? t('profile.sponsor_actionUpdate') : t('profile.sponsor_confirm')}
                onPress={onActivateSponsor}
                loading={sponsorBusy}
                disabled={sponsorRateNum < sponsorRateFloor}
              />
              {!!sponsoredListing && (
                <Button title={t('profile.sponsor_actionCancel')} variant="secondary" accentColor={colors.danger} onPress={onCancelSponsor} loading={sponsorBusy} />
              )}
            </Pressable>
          </Pressable>
          </KeyboardAvoidingView>
        </Modal>
      )}

      {showInvitePartner && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setShowInvitePartner(false)}>
          <Pressable style={[styles.regionModalOverlay, { paddingBottom: keyboardHeight }]} onPress={() => setShowInvitePartner(false)}>
            <Pressable style={styles.regionModalCard} onPress={() => {}}>
              <Text style={styles.regionModalTitle}>{t('profile.business_invitePartner')}</Text>
              <View style={styles.regionSearchWrap}>
                <Ionicons name="search" size={15} color={colors.textMuted} />
                <TextInput
                  style={styles.regionSearchInput}
                  value={partnerQuery}
                  onChangeText={setPartnerQuery}
                  placeholder={t('profile.business_searchPartnerPlaceholder')}
                  placeholderTextColor={colors.textMuted}
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>
              <View style={styles.regionSearchWrap}>
                <Ionicons name="pie-chart-outline" size={15} color={colors.textMuted} />
                <TextInput
                  style={styles.regionSearchInput}
                  value={partnerEquityPercent}
                  onChangeText={setPartnerEquityPercent}
                  placeholder={t('profile.business_equityPlaceholder')}
                  placeholderTextColor={colors.textMuted}
                  keyboardType="decimal-pad"
                />
              </View>
              <ScrollView style={{ maxHeight: 320 }} keyboardShouldPersistTaps="handled">
                {partnerResults.map((p) => (
                  <Pressable
                    key={p.id}
                    style={styles.regionRow}
                    disabled={invitingPartnerId === p.id}
                    onPress={() => void onInvitePartner(p.id)}
                  >
                    <Text style={styles.regionRowText}>{p.full_name || t('common.user')}</Text>
                    {invitingPartnerId === p.id
                      ? <Text style={styles.toggleSublabel}>{t('common.saving')}</Text>
                      : <Ionicons name="add-circle-outline" size={18} color={colors.primary} />}
                  </Pressable>
                ))}
                {partnerQuery.trim().length >= 2 && partnerResults.length === 0 && !partnerQuery.includes('@') && (
                  <Text style={[styles.regionRowText, { textAlign: 'center', paddingVertical: spacing.md, color: colors.textMuted }]}>
                    {t('common.noResults')}
                  </Text>
                )}
                {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partnerQuery.trim()) && partnerResults.length === 0 && (
                  <Pressable
                    style={styles.regionRow}
                    disabled={invitingByEmail}
                    onPress={() => void onInvitePartnerByEmail()}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={styles.regionRowText}>{t('profile.business_inviteByEmail', { email: partnerQuery.trim() })}</Text>
                      <Text style={styles.toggleSublabel}>{t('profile.business_inviteByEmailHint')}</Text>
                    </View>
                    {invitingByEmail
                      ? <Text style={styles.toggleSublabel}>{t('common.saving')}</Text>
                      : <Ionicons name="mail-outline" size={18} color={colors.primary} />}
                  </Pressable>
                )}
              </ScrollView>
              <Pressable style={styles.regionModalClose} onPress={() => { setShowInvitePartner(false); setPartnerEquityPercent(''); }}>
                <Text style={styles.regionModalCloseText}>{t('common.close')}</Text>
              </Pressable>
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    scrollFlex: { flex: 1 },
    content: { paddingHorizontal: spacing.lg, gap: spacing.md },
    topBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs },
    back: { color: colors.primary, fontSize: fontSize.md, fontWeight: '600' },
    title: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    spacer: { width: 44 },
    businessCard: {
      borderRadius: radius.md, borderWidth: 1, padding: spacing.md, gap: spacing.sm,
    },
    businessCardOwned: {
      backgroundColor: `${colors.primary}12`, borderColor: `${colors.primary}55`,
    },
    businessCardPartner: {
      backgroundColor: colors.surfaceAlt, borderColor: colors.border, opacity: 0.85,
    },
    businessOwnedTag: {
      color: colors.primary, fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.4,
    },
    businessPartnerTag: {
      color: colors.textMuted, fontSize: 11, fontWeight: '700',
    },
    businessNameHeading: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    businessMemberRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    businessMemberName: { flex: 1, color: colors.text, fontSize: fontSize.sm, fontWeight: '600' },
    secondaryCategoryChips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.xs },
    secondaryCategoryChip: {
      flexDirection: 'row', alignItems: 'center', gap: 6,
      backgroundColor: colors.surfaceAlt, borderWidth: 1, borderColor: colors.border,
      borderRadius: radius.full, paddingHorizontal: spacing.sm, paddingVertical: 5,
    },
    secondaryCategoryChipText: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600' },
    businessOwnerBadge: {
      backgroundColor: `${colors.primary}18`, borderRadius: radius.full,
      paddingHorizontal: 8, paddingVertical: 2,
    },
    businessOwnerBadgeText: { color: colors.primary, fontSize: 10, fontWeight: '800' },
    businessEquityText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '700' },
    businessRepresentativeBadge: {
      flexDirection: 'row', alignItems: 'center', gap: 3,
      backgroundColor: `${colors.success}18`, borderRadius: radius.full,
      paddingHorizontal: 7, paddingVertical: 2,
    },
    businessRepresentativeBadgeText: { color: colors.success, fontSize: 9.5, fontWeight: '800' },
    addressVisibilityToggle: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
      marginTop: spacing.xs,
    },
    addressVisibilityToggleText: { flex: 1, color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '600' },
    identityPickerBox: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.sm,
    },
    identityPickerTitle: { color: colors.text, fontSize: fontSize.sm, fontWeight: '700' },
    identityPickerHint: { color: colors.textMuted, fontSize: fontSize.xs, marginBottom: 2 },
    identityCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      borderWidth: 1.5,
      borderColor: colors.border,
      borderRadius: radius.md,
      padding: spacing.sm,
    },
    identityCardSelected: { borderColor: colors.success, backgroundColor: colors.surfaceAlt },
    identityCardTitle: { color: colors.text, fontSize: fontSize.sm, fontWeight: '700' },
    identityCardTitleSelected: { color: colors.success },
    identityCardSubtitle: { color: colors.textMuted, fontSize: fontSize.xs, marginTop: 1 },
    businessLeaveText: { color: colors.danger, fontSize: fontSize.sm, fontWeight: '600', textAlign: 'center', marginTop: spacing.xs },
    businessInviteCard: {
      backgroundColor: `${colors.primary}14`, borderWidth: 1, borderColor: `${colors.primary}40`,
      borderRadius: radius.md, padding: spacing.md, gap: spacing.sm,
    },
    businessInviteText: { color: colors.text, fontSize: fontSize.sm, lineHeight: 19 },
    businessInviteActions: { flexDirection: 'row', gap: spacing.sm, justifyContent: 'flex-end' },
    businessInviteDecline: { paddingVertical: 6, paddingHorizontal: spacing.md },
    businessInviteDeclineText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    businessInviteAccept: { backgroundColor: colors.primary, borderRadius: radius.full, paddingVertical: 6, paddingHorizontal: spacing.md },
    businessInviteAcceptText: { color: colors.textOnPrimary, fontSize: fontSize.sm, fontWeight: '700' },
    regionModalOverlay: {
      flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', padding: spacing.lg,
    },
    regionModalCard: {
      backgroundColor: colors.surface, borderRadius: radius.lg, padding: spacing.lg, gap: spacing.sm,
    },
    regionModalTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700', marginBottom: spacing.xs },
    regionSearchWrap: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
      backgroundColor: colors.surfaceAlt, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border,
      paddingHorizontal: spacing.sm, height: 44, marginBottom: spacing.xs,
    },
    regionSearchInput: { flex: 1, color: colors.text, fontSize: fontSize.md },
    regionRow: {
      flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
      paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border,
    },
    regionRowText: { color: colors.text, fontSize: fontSize.md },
    regionModalClose: { alignItems: 'center', paddingVertical: spacing.sm, marginTop: spacing.xs },
    regionModalCloseText: { color: colors.primary, fontSize: fontSize.md, fontWeight: '700' },
    floatBtn: {
      position: 'absolute',
      right: spacing.lg,
      alignItems: 'flex-end',
      zIndex: 50,
    },
    floatBtnInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      backgroundColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: 8,
      paddingHorizontal: spacing.md,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 10,
      elevation: 8,
    },
    floatBtnIcon: { fontSize: 13 },
    floatBtnText: { color: colors.textOnPrimary, fontSize: fontSize.xs, fontWeight: '700' },
    profileModeTabs: {
      flexDirection: 'row',
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: 'hidden',
      marginBottom: spacing.sm,
    },
    profileModeTab: {
      flex: 1,
      flexDirection: 'row',
      paddingVertical: 10,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
    },
    profileModeTabActive: { backgroundColor: colors.primary },
    profileModeTabText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    profileModeTabTextActive: { color: colors.textOnPrimary },
    toggleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: spacing.xs,
      gap: spacing.md,
    },
    toggleLabelRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flex: 1 },
    toggleLabel: { color: colors.text, fontSize: fontSize.md, fontWeight: '500', flex: 1, flexShrink: 1 },
    toggleSublabel: { color: colors.textMuted, fontSize: fontSize.xs, marginTop: 1 },
    toggle: {
      width: 44,
      height: 26,
      borderRadius: 13,
      backgroundColor: colors.border,
      padding: 3,
      flexShrink: 0,
    },
    toggleOn: { backgroundColor: colors.primary },
    knob: { width: 20, height: 20, borderRadius: 10, backgroundColor: colors.text },
    knobOn: { transform: [{ translateX: 18 }] },
    viewBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      marginTop: spacing.xs,
    },
    viewBtnText: { flex: 1, color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    fieldSection: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.md,
      gap: spacing.xs,
    },
    fieldLabelRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    fieldLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '500' },
    fieldInput: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      color: colors.text,
      fontSize: fontSize.md,
    },
    ibanRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.sm },
    ibanBankInput: { flex: 1 },
    ibanIbanInput: { flex: 2 },
    ibanRemoveBtn: { padding: 2 },
    ibanAddBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.border,
      borderStyle: 'dashed',
      borderRadius: radius.md,
      paddingVertical: 8,
      marginTop: spacing.sm,
    },
    ibanAddBtnText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '700' },
    logoPreview: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
    logoChangeBtn: {
      paddingVertical: 6,
      paddingHorizontal: spacing.sm,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    logoChangeBtnText: { color: colors.primaryLight, fontSize: fontSize.sm, fontWeight: '600' },
    logoUploadBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: 9,
      paddingHorizontal: spacing.md,
      backgroundColor: 'rgba(99,102,241,0.08)',
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: 'rgba(99,102,241,0.2)',
      alignSelf: 'flex-start',
    },
    logoUploadBtnText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    wizardSetupBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      marginTop: spacing.xs,
    },
    wizardSetupBtnText: { flex: 1, color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    sponsorBox: {
      backgroundColor: colors.surface,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.xs,
      marginTop: spacing.xs,
    },
    sponsorHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    sponsorTitle: { color: colors.text, fontSize: fontSize.sm, fontWeight: '700' },
    sponsorSublabel: { color: colors.textMuted, fontSize: fontSize.xs },
    sponsorActiveText: { color: colors.success, fontSize: fontSize.xs, fontWeight: '600' },
    sponsorBtn: {
      alignSelf: 'flex-start',
      backgroundColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: 6,
      paddingHorizontal: spacing.md,
      marginTop: 2,
    },
    sponsorBtnText: { color: colors.textOnPrimary, fontSize: fontSize.xs, fontWeight: '700' },
    sponsorTierRow: { flexDirection: 'row', gap: spacing.sm, marginVertical: spacing.sm },
    sponsorTierOption: {
      flex: 1,
      borderWidth: 1.5,
      borderColor: colors.border,
      borderRadius: radius.md,
      padding: spacing.sm,
      gap: 2,
    },
    sponsorTierOptionSelected: { borderColor: colors.primary, backgroundColor: colors.surfaceAlt },
    sponsorTierText: { color: colors.text, fontSize: fontSize.xs, fontWeight: '700' },
    sponsorTierPrice: { color: colors.textMuted, fontSize: 11 },
    sponsorTierTextSelected: { color: colors.primary },
    sponsorCostText: { color: colors.text, fontSize: fontSize.sm, fontWeight: '700', marginTop: spacing.xs },
    sponsorErrorText: { color: colors.danger, fontSize: fontSize.xs },
    sponsorRateSuffix: { color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '600' },
    sponsorOutbidBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      alignSelf: 'flex-start',
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: 4,
      paddingHorizontal: spacing.sm,
    },
    sponsorOutbidText: { color: colors.primary, fontSize: fontSize.xs, fontWeight: '700' },
    sponsorNightlyNote: { color: colors.textMuted, fontSize: 11, lineHeight: 15 },
    sponsorWhereBox: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      padding: spacing.sm,
      gap: 4,
    },
    sponsorWhereTitle: { color: colors.textMuted, fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
    sponsorWhereRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 6 },
    sponsorWhereText: { flex: 1, color: colors.text, fontSize: fontSize.xs, lineHeight: 16 },
    wizardReviewCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.lg,
      gap: spacing.md,
      ...shadow.card,
    },
    wizardReviewTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    wizardReviewRow: { flexDirection: 'row', justifyContent: 'space-between', gap: spacing.sm },
    wizardReviewLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '500' },
    wizardReviewValue: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600', flex: 1, textAlign: 'right' },
  });
}
